import { type SVGProps, useEffect, useMemo, useState } from 'react'
import { CircularProgress, IconButton, Stack, Tooltip } from '@mui/material'

import { InlinePopoverEditor } from './InlinePopoverEditor'

import { type CardActivityWithId } from '@/models/card-activity/card-activity.model'
import { debounce } from '@/hooks/useDebounce'
import translateText from '@/utils/translateText'
import useUtils from '@/hooks/useUtilities'

interface RepeatTranslationProps {
  card: CardActivityWithId
  updateCard: (id: string, field: keyof CardActivityWithId, value: Partial<CardActivityWithId>) => void
}

export default function RepeatTranslation({ card, updateCard }: RepeatTranslationProps) {
  const { showSnackbar } = useUtils()
  const [loadingTranslation, setLoadingTranslation] = useState(false)
  const debouncedUpdateCard = useMemo(() => {
    return debounce((id: string, field: keyof CardActivityWithId, value: Partial<CardActivityWithId>) => {
      updateCard(id, field, value)
    }, 500)
  }, [updateCard])

  const handleChange = (value: string) => {
    debouncedUpdateCard(card.id, 'native_text', value)
  }
  const generateTranslation = async () => {
    setLoadingTranslation(true)
    try {
      const translatedText = await translateText({
        target_text: card?.target_text,
        toLanguage: card?.default_language,
        language: card?.language,
      })
      const cleanedTranslatedText = translatedText.replace(/^"(.*)"$/, '$1')

      handleChange(cleanedTranslatedText)
      showSnackbar('Translation generated successfully!', 'success')
    } catch (e) {
      console.error('generateTranslation', e)
    } finally {
      setLoadingTranslation(false)
    }
  }

  useEffect(() => {
    return () => {
      debouncedUpdateCard.cancel?.()
    }
  }, [debouncedUpdateCard])

  return (
    <Stack
      alignItems="center"
      direction="row"
      gap="4px"
      onClick={e => {
        e.preventDefault()
        e.stopPropagation()
      }}
    >
      <Tooltip title={loadingTranslation ? 'Generating Translation...' : 'Generate Translation'}>
        <span>
          <IconButton disabled={loadingTranslation} onClick={generateTranslation}>
            {loadingTranslation ? <CircularProgress size={21} /> : <AIGenerateIcon />}
          </IconButton>
        </span>
      </Tooltip>
      <InlinePopoverEditor
        label={card?.native_text || 'Click to edit'}
        placeholder="Enter here..."
        showUnderline={false}
        value={card?.native_text}
        onSave={newValue => {
          handleChange(newValue)
        }}
      />
    </Stack>
  )
}

export function AIGenerateIcon(props: SVGProps<SVGSVGElement>) {
  return (
    <svg fill="none" height={21} viewBox="0 0 21 21" width={21} xmlns="http://www.w3.org/2000/svg" {...props}>
      <g clipPath="url(#a)">
        <path
          d="M15.51 6.754V2.587m-10.834 15v-4.166m8.75-8.75h4.167m-15 10.833H6.76M5.51 1.754 4.856 3.06c-.221.443-.332.664-.48.856a2.5 2.5 0 0 1-.453.454c-.192.147-.413.258-.856.48l-1.307.653 1.307.654c.443.22.664.331.856.48a2.5 2.5 0 0 1 .453.453c.148.192.259.413.48.855l.654 1.308.653-1.308c.222-.442.332-.663.48-.855a2.5 2.5 0 0 1 .454-.454c.192-.148.413-.258.855-.48l1.308-.653-1.308-.654c-.442-.221-.663-.332-.855-.48a2.5 2.5 0 0 1-.454-.453c-.148-.192-.258-.413-.48-.856zm8.75 8.333-.793 1.585c-.221.443-.332.664-.48.856a2.5 2.5 0 0 1-.453.454c-.192.147-.413.258-.856.48l-1.585.792 1.585.793c.443.22.664.331.856.48q.255.197.454.453c.147.192.258.413.48.855l.792 1.586.792-1.586c.222-.442.332-.663.48-.855q.198-.256.454-.454c.191-.148.413-.258.855-.48l1.585-.792-1.585-.793c-.442-.22-.664-.332-.855-.48a2.5 2.5 0 0 1-.454-.453c-.148-.192-.258-.413-.48-.856z"
          stroke="url(#b)"
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={1.667}
        />
      </g>
      <defs>
        <linearGradient gradientUnits="userSpaceOnUse" id="b" x1={1.76} x2={18.426} y1={18.421} y2={1.754}>
          <stop stopColor="#FF6C6C" />
          <stop offset={1} stopColor="#DD7BFF" />
        </linearGradient>
        <clipPath id="a">
          <path d="M.093.086h20v20h-20z" fill="#fff" />
        </clipPath>
      </defs>
    </svg>
  )
}
